import {
  QueryFunction,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { TError, useHandleErrors } from './useHandleErrors';

type TQueryKey = unknown[];

type CustomQueryOptions<TData> = Omit<
  UseQueryOptions<TData, TError, TData, TQueryKey>,
  'queryFn' | 'queryKey' | 'onSuccess' | 'onError' | 'onSettled'
> & {
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
};

export const useCustomQuery = <TData>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TData, TQueryKey>,
  options: CustomQueryOptions<TData> = {}
) => {
  const { handleErrorsOnError, handleErrorsOnSuccess } = useHandleErrors();
  const { onSuccess, onError, ...queryOptions } = options;
  const operation = typeof queryKey[0] === 'string' ? queryKey[0] : undefined;

  const query = useQuery({
    ...queryOptions,
    queryKey,
    queryFn,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!query.isSuccess) {
      return;
    }

    handleErrorsOnSuccess(query.data, queryKey, operation);
    onSuccess?.(query.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.data]);

  useEffect(() => {
    if (!query.isError) {
      return;
    }

    handleErrorsOnError(query.error, queryKey, operation);
    onError?.(query.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  return query;
};
